import React, { useEffect } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import { sortBy } from 'lodash';
import { hashHistory } from 'react-router';

import { BackLink, Typography } from '../../components/atoms';
import { LoadingSpinner, Page } from '../../components/layouts';
import { NavList } from '../../components/molecules';
import { NavListVariantType } from '../../components/molecules/NavListItem/NavListItem';
import * as Api from '../../weborder/Api';
import { RouteProps, StateProps } from '../../weborder/component/DeliveryList';
import * as Store from '../../weborder/Store';
import { domUtils } from '../../weborder/utils';
import {
  isRestaurantClosedWithNoPreOrders,
  renderAvailabilityStatusIcon,
} from '../StoreCollectionList/utils/StoreListUtils';

type Props = StateProps & RouteProps;

const useStyles = makeStyles((theme: Theme) => ({
  disabled: {
    '& .MuiTypography-root': {
      color: theme.palette.grey[600],
    },
  },
  preOrder: {
    color: theme.palette.warning.main,
  },
  restaurantDetails: {
    color: theme.palette.grey[800],
  },
  restaurantTitle: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(0.25),

    '& .fd-icon': {
      marginRight: theme.spacing(1),
    },
  },
}));

export function StoreDeliveryList(props: Props): JSX.Element | null {
  const { deliveryOption, deliveryRestaurants, params, translate } = props;

  const classes = useStyles();

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  if (!deliveryOption || deliveryOption.status !== 'success') {
    return <LoadingSpinner />;
  }

  if (deliveryOption.data.type === 'skip') {
    hashHistory.replace(
      `/restaurant/${deliveryOption.data.to.VirtualRestaurantId}/delivery/${params.location}`,
    );
    return null;
  }

  const getStoreUrl = (r: Api.DeliveryRestaurantSummary): string =>
    `/restaurant/${r.VirtualRestaurantId}/delivery/${params.location}`;

  const getRestaurantDetails = (r) => {
    const detail =
      deliveryRestaurants[
        Store.DeliveryKey(r.VirtualRestaurantId, params.location)
      ];
    return detail;
  };

  const getRestaurantItem = (r: Api.DeliveryRestaurantSummary) => {
    const detail = getRestaurantDetails(r);

    if (detail === undefined) {
      return null;
    }

    let restaurantAddress: JSX.Element | null = null;
    let closedNotice: JSX.Element | null = null;
    let restaurantName: JSX.Element;

    let address = '';
    let message = '';
    let openingStatus;
    let preorderStatus;

    if (detail.status === 'success') {
      const {
        data: {
          AcceptPreOrders,
          IsOpen,
          OpenTimeMessage,
          PhysicalRestaurantAddress,
        },
      } = detail;

      address = PhysicalRestaurantAddress;
      message = OpenTimeMessage;
      openingStatus = IsOpen;
      preorderStatus = AcceptPreOrders;

      const getRestaurantName = () => {
        return (
          <div className={classes.restaurantTitle}>
            {renderAvailabilityStatusIcon(detail.data)}
            <Typography>
              <em>{r.Name}</em>
            </Typography>
          </div>
        );
      };

      const getAddress = () => {
        if (!address) {
          return null;
        }
        return (
          <Typography className={classes.restaurantDetails} variant="body2">
            {address}
          </Typography>
        );
      };

      const getClosedNotice = () => {
        let preOrder = '';

        if (openingStatus) {
          return null;
        }

        if (preorderStatus) {
          preOrder = translate('StoreCollectionList_PreorderNow');
        }

        return (
            <Typography className={classes.restaurantDetails} variant="body2">
              {preOrder ? (
                <span className={classes.preOrder}>{preOrder} </span>
              ) : null}
              {message}
            </Typography>
        );
      };

      restaurantName = getRestaurantName();
      restaurantAddress = getAddress();
      closedNotice = getClosedNotice();
    }

    const closedStatusStyles =
      !openingStatus && !preorderStatus ? classes.disabled : null;

    const itemVariant: NavListVariantType =
      !openingStatus && !preorderStatus ? 'noEndIcon' : 'default';

    const restaurantDetails = {
      elements: [
        <div
          className={closedStatusStyles}
          key={`store-${r.PhysicalRestaurantId}`}
        >
          {restaurantName}
          {restaurantAddress}
          {closedNotice}
        </div>,
      ],
      linkId: getStoreUrl(r),
      linkPath: getStoreUrl(r),
      variant: itemVariant,
    };

    return restaurantDetails;
  };

  const sortedRestaurants = sortBy(deliveryOption.data.of, (restaurant) =>
    isRestaurantClosedWithNoPreOrders(restaurant),
  );

  const restaurantItems = sortedRestaurants
    .map((r) => getRestaurantItem(r))
    .filter((r) => r !== null);

  return (
    <Page pageName="store-delivery-list">
      <header>
        <BackLink label={translate('Back')} linkPath={'/where'} />

        <Typography variant="h1">{translate('Order_delivery_from')}</Typography>
      </header>

      <article>
        <NavList items={restaurantItems}></NavList>
      </article>
    </Page>
  );
}
